import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useState, useCallback, useReducer, useMemo, useEffect } from 'react'
import { DESKTOP_BREAKPOINT } from '~/common/constants/layout-constants'
import { TpBanner } from '~/components'
import { reportFormReducer, initialReportFormState } from './report-form-reducer'
import { useReportTypes } from './hooks/use-report-types'
import { useSubmitReport } from './hooks/use-submit-report'
import { useFrostReportCascade } from './hooks/use-frost-report-cascade'
import { FrostDistributionChart } from './components/frost-distribution-chart'
import { supabaseGetter } from '~/api/supabase-getter'
import { matchSorter } from 'match-sorter'
import { useFarmId } from '~/common/use-farm-id'
import { useClimate } from '~/crop/crop-schedule/hooks/use-climate'
import { doyToJSDate } from '~/common/utils/doy-utils'

/**
 * Modal for submitting farm reports (frost dates, weather events, etc.).
 *
 * @param {boolean} open
 * @param {function} onClose
 */
export const ReportModal = ({ open, onClose }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_BREAKPOINT))
  const { farmId } = useFarmId()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lfdMinDate, setLfdMinDate] = useState(null)
  const [form, dispatch] = useReducer(reportFormReducer, { ...initialReportFormState, reportDate: dayjs() })
  const { reportCode, reportDate, notes, existingReportId, originalReportDate } = form

  const { reportTypes } = useReportTypes()
  const { runCascade } = useFrostReportCascade()
  const { climateData } = useClimate({ farmId })

  const { submitReport, loading } = useSubmitReport({ reportCode, reportDate, notes, farmId, existingReportId })

  const selectedType = useMemo(
    () => reportTypes.find(t => t.code === reportCode) ?? null,
    [reportTypes, reportCode]
  )

  const isEditing = Boolean(existingReportId)
  const isFrostReport = reportCode === 'lfd' || reportCode === 'ffd'

  useEffect(() => {
    async function fetchLfd() {
      if (reportCode !== 'ffd' || !farmId) {
        setLfdMinDate(null)
        return
      }

      const { data } = await supabaseGetter.reports.getByOrganizationAndCode(farmId, 'lfd')
      setLfdMinDate(data?.created_at ? dayjs(data.created_at) : null)
    }
    fetchLfd()
  }, [reportCode, farmId])

  // For single_submission report types, check if the farm already has a report.
  // If yes, populate the form and enter edit mode.
  useEffect(() => {
    if (!selectedType?.single_submission || !farmId || !reportCode) return

    supabaseGetter.reports.getByOrganizationAndCode(farmId, reportCode).then(({ data }) => {
      if (data) dispatch({ type: 'INIT_FROM_REPORT', report: data })
    })
  }, [reportCode, farmId, selectedType?.single_submission])

  const reportWarning = reportCode === 'lfd'
    ? 'This frost report will BEGIN the growing season and update all schedule estimates for all active crops.'
    : reportCode === 'ffd'
      ? 'This frost report will END the growing season and update all schedule estimates for all active crops.'
      : ''

  const filterReportOptions = (options, { inputValue }) => {
    if (!inputValue) return options
    return matchSorter(options.filter(Boolean), inputValue, { keys: ['name'] })
  }

  const _onClose = useCallback(() => {
    setIsSubmitting(false)
    dispatch({ type: 'RESET' })
    onClose?.()
  }, [onClose])

  const onSubmit = useCallback(async () => {
    setIsSubmitting(true)
    await submitReport({
      onSuccess: async () => {
        if (isFrostReport) {
          const newDate = reportDate.toDate()
          // Determine the old anchor: previous report date (edit) or climate average (new)
          const oldDate = existingReportId
            ? new Date(originalReportDate)
            : doyToJSDate(reportCode === 'lfd' ? climateData?.avg_lfd : climateData?.avg_ffd)
          await runCascade({ farmId, reportCode, newDate, oldDate })
        }
        _onClose()
      },
      onError: () => setIsSubmitting(false),
    })
  }, [
    submitReport,
    isFrostReport,
    reportDate,
    existingReportId,
    originalReportDate,
    reportCode,
    climateData,
    runCascade,
    farmId,
    _onClose
  ])

  const isValid = Boolean(reportCode)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ClosingAwareModal
        open={open}
        onClose={_onClose}
        loading={isSubmitting}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          py: 4,
        }}
      >
        <Box
          component="form"
          sx={{
            position: 'relative',
            width: { xs: '90vw', md: '80vw', lg: '60vw' },
            bgcolor: 'background.paper',
            borderRadius: '0.5rem',
            boxShadow: 24,
            p: '1rem 2rem',
            my: 'auto',
          }}
        >
          {/* Title row with optional Editing chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: '0.5rem' }}>
            <Typography variant="h4">Report</Typography>
            {isEditing && (
              <Chip label="Editing" size="small" color="info" variant="outlined" />
            )}
          </Box>
          <Divider variant="fullWidth" />

          {/* Row 1: Report type + date */}
          <Grid container spacing="1rem" rowSpacing="0.5rem" sx={{ pt: '1rem' }}>
            <Grid size={{ xs: 12, sm: 7 }}>
              <Autocomplete
                size={isDesktop ? 'medium' : 'small'}
                options={reportTypes}
                value={selectedType}
                onChange={(_, newValue) => {
                  // Always reset first so existingReportId is cleared, then set the new code.
                  // The useEffect above will re-populate if the new type is single_submission.
                  dispatch({ type: 'RESET' })
                  if (newValue?.code) {
                    dispatch({ type: 'SET', key: 'reportCode', value: newValue.code })
                  }
                }}
                filterOptions={filterReportOptions}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderInput={(params) => (
                  <TextField {...params} label="Report Type" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <DatePicker
                label="Date"
                minDate={lfdMinDate ?? undefined}
                maxDate={dayjs()}
                value={reportDate}
                onChange={(newDate) => dispatch({ type: 'SET', key: 'reportDate', value: newDate })}
                slotProps={{
                  textField: {
                    size: isDesktop ? 'medium' : 'small',
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Row 2: Selected type description */}
          <Box sx={{ pt: '0.75rem', minHeight: '1.5rem' }}>
            {selectedType?.description && (
              <Typography variant="body2" color="text.secondary">
                {selectedType.description}
              </Typography>
            )}
          </Box>

          {/* Row 3: Frost distribution chart (lfd / ffd only) */}
          {isFrostReport && (
            <Box sx={{ pt: '0.75rem' }}>
              <FrostDistributionChart
                reportCode={reportCode}
                reportDate={reportDate}
                farmId={farmId}
              />
            </Box>
          )}

          {/* Row 4: Notes */}
          <Box>
            <TextField
              label="Notes"
              multiline
              minRows={2}
              maxRows={4}
              variant="filled"
              fullWidth
              value={notes}
              slotProps={{ htmlInput: { style: { resize: 'none' } } }}
              onChange={(e) => dispatch({ type: 'SET', key: 'notes', value: e.target.value || '' })}
            />
          </Box>

          {/* Footer: warning (left) + buttons (right) */}
          <Box
            sx={{
              mt: '1rem',
              display: 'flex',
              gap: '1rem',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              {reportWarning && <TpBanner severity="warning" message={reportWarning} />}
            </Box>
            <Box sx={{ display: 'flex', gap: '1rem', flexShrink: 0 }}>
              <Button variant="outlined" size="large" onClick={_onClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="large"
                disabled={!isValid || loading}
                onClick={onSubmit}
              >
                {isEditing ? 'Update' : 'Report'}
              </Button>
            </Box>
          </Box>
        </Box>
      </ClosingAwareModal>
    </LocalizationProvider>
  )
}
